import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../Firebase";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

export default function useUserData() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersList = [];

        usersSnapshot.forEach((doc) => {
          const userData = { id: doc.id, ...doc.data() };
          if (!userData.registrationDate) {
            userData.registrationDate = Date.now();
          }
          usersList.push(userData);
        });

        setUsers(usersList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching users: ", error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null) return "N/A";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    let formattedTime = "";
    if (hours > 0) formattedTime += `${hours}hr `;
    if (minutes > 0 || hours > 0) formattedTime += `${minutes}min`;
    return formattedTime || "0min";
  };

  const formatDate = (timestamp) => {
    return timestamp ? new Date(timestamp).toLocaleDateString() : "N/A";
  };

  const Author = ({ name, image }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      {image ? (
        <MDAvatar
          src={image}
          name={name}
          size="sm"
          style={{ width: "50px", height: "100%", borderRadius: "0px" }}
        />
      ) : (
        <MDTypography variant="caption">No Image</MDTypography>
      )}
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  const ImageRenderer = ({ imageUrl }) => {
    return imageUrl ? (
      <img
        src={imageUrl}
        alt="God Mala"
        style={{ width: "50px", height: "auto", objectFit: "cover" }}
      />
    ) : (
      <MDTypography variant="caption">No Image</MDTypography>
    );
  };

  Author.propTypes = {
    name: PropTypes.string.isRequired,
    image: PropTypes.string,
  };

  ImageRenderer.propTypes = {
    imageUrl: PropTypes.string,
  };

  const columns = [
    { Header: "user", accessor: "user", width: "20%", align: "left" },
    { Header: "contact", accessor: "contact", align: "left" },
    { Header: "activity", accessor: "activity", align: "center" },
    { Header: "mala count", accessor: "malaCount", align: "center" },
    { Header: "registered", accessor: "registered", align: "center" },
    { Header: "collection request", accessor: "isCollectionRequestSubmitted", align: "center" },
    { Header: "deleted", accessor: "isDeleted", align: "center" },
    { Header: "Instagram following", accessor: "isFollowingOnInstagram", align: "center" },
    { Header: "YouTube subscription", accessor: "isSubscribedOnYoutube", align: "center" },
    { Header: "God Mala image", accessor: "selectedGodMalaImage", align: "center" },
  ];

  const rows = users.map((user) => ({
    user: (
      <Author
        name={`${user.firstName || ""} ${user.lastName || ""}`}
        image={user.profileImage || ""}
      />
    ),
    contact: (
      <MDTypography variant="caption">
        {user.countryCode || ""} {user.mobileNumber || ""}
      </MDTypography>
    ),
    activity: (
      <MDBox display="flex" flexDirection="column">
        <MDTypography variant="caption">Tasks: {formatTime(user.dailyTaskWatchTime)}</MDTypography>
        <MDTypography variant="caption">
          Videos: {formatTime(user.dailyVideoWatchTime)}
        </MDTypography>
      </MDBox>
    ),
    malaCount: (
      <MDTypography variant="caption" fontWeight="medium">
        {user.malaCount || 0}
      </MDTypography>
    ),
    registered: <MDTypography variant="caption">{formatDate(user.registrationDate)}</MDTypography>,
    isCollectionRequestSubmitted: (
      <MDTypography variant="caption">
        {user.isCollectionRequestSubmitted ? "Yes" : "No"}
      </MDTypography>
    ),
    isDeleted: <MDTypography variant="caption">{user.isDeleted ? "Yes" : "No"}</MDTypography>,
    isFollowingOnInstagram: (
      <MDTypography variant="caption">{user.isFollowingOnInstagram ? "Yes" : "No"}</MDTypography>
    ),
    isSubscribedOnYoutube: (
      <MDTypography variant="caption">{user.isSubscribedOnYoutube ? "Yes" : "No"}</MDTypography>
    ),
    selectedGodMalaImage: <ImageRenderer imageUrl={user.selectedGodMalaImage} />,
  }));

  return {
    columns,
    rows,
    loading,
    totalUsers: users.length,
    users,
  };
}
